
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Clock, MapPin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import { BikeMovementDetails } from './BikeMovementDetails';

interface BikeMovement {
  id: string;
  model: string;
  serial_number: string;
  status: string;
  movement_count: number;
  last_update: string;
  current_latitude: number;
  current_longitude: number;
  alert_level: 'none' | 'low' | 'medium' | 'high';
}

export const BikeMovementMonitoring: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [bikes, setBikes] = useState<BikeMovement[]>([]);
  const [timeframe, setTimeframe] = useState<string>('24');
  const [error, setError] = useState<string | null>(null);
  const [selectedBikeId, setSelectedBikeId] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchBikeMovements(timeframe);
  }, [timeframe]);

  const fetchBikeMovements = async (hours: string) => {
    setLoading(true);
    try {
      // Получаем текущую дату и дату "hours" часов назад
      const now = new Date();
      const pastDate = new Date(now.getTime() - parseInt(hours) * 60 * 60 * 1000);

      // Получаем все доступные велосипеды
      const { data: availableBikes, error: bikesError } = await supabase
        .from('bikes')
        .select('id, model, serial_number, status')
        .eq('status', 'available');

      if (bikesError) throw bikesError;
      if (!availableBikes || availableBikes.length === 0) {
        setBikes([]);
        setLoading(false);
        return;
      }

      // Получаем данные о движении для каждого доступного велосипеда
      const bikeIds = availableBikes.map(bike => bike.id);
      const { data: movementData, error: movementError } = await supabase
        .from('bike_tracking_history')
        .select('bike_id, created_at, latitude, longitude')
        .in('bike_id', bikeIds)
        .gte('created_at', pastDate.toISOString())
        .order('created_at', { ascending: false });

      if (movementError) throw movementError;

      // Обрабатываем данные для отображения
      const processedData: BikeMovement[] = availableBikes.map(bike => {
        const bikeMovements = movementData?.filter(m => m.bike_id === bike.id) || [];
        const movementCount = bikeMovements.length;
        const lastUpdate = bikeMovements.length > 0 ? bikeMovements[0].created_at : null;
        const currentLocation = bikeMovements.length > 0 ? 
          { lat: bikeMovements[0].latitude, lng: bikeMovements[0].longitude } : 
          { lat: 0, lng: 0 };

        // Определяем уровень тревоги на основе количества движений
        let alertLevel: 'none' | 'low' | 'medium' | 'high' = 'none';
        if (movementCount > 0) {
          if (movementCount > 10) {
            alertLevel = 'high';
          } else if (movementCount > 5) {
            alertLevel = 'medium';
          } else {
            alertLevel = 'low';
          }
        }

        return {
          id: bike.id,
          model: bike.model,
          serial_number: bike.serial_number || 'Неизвестно',
          status: bike.status,
          movement_count: movementCount,
          last_update: lastUpdate,
          current_latitude: currentLocation.lat,
          current_longitude: currentLocation.lng,
          alert_level: alertLevel
        };
      });

      // Сортируем по количеству движений (сначала велосипеды с наибольшим движением)
      const sortedData = processedData.sort((a, b) => b.movement_count - a.movement_count);
      setBikes(sortedData);
    } catch (err: any) {
      console.error("Ошибка при получении данных о движении велосипедов:", err);
      setError(err.message || "Произошла ошибка при загрузке данных");
    } finally {
      setLoading(false);
    }
  };

  const getAlertBadge = (alertLevel: 'none' | 'low' | 'medium' | 'high') => {
    switch (alertLevel) {
      case 'high':
        return <Badge className="bg-red-500">Высокий</Badge>;
      case 'medium':
        return <Badge className="bg-amber-500">Средний</Badge>;
      case 'low':
        return <Badge className="bg-yellow-400">Низкий</Badge>;
      default:
        return <Badge className="bg-green-500">Нет</Badge>;
    }
  };

  const formatLastUpdate = (dateString: string | null) => {
    if (!dateString) return 'Нет данных';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: ru });
    } catch (e) {
      return 'Некорректная дата';
    }
  };

  const viewDetails = (bikeId: string) => {
    setSelectedBikeId(bikeId);
    setShowDetails(true);
  };

  const handleRefresh = () => {
    fetchBikeMovements(timeframe);
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <CardTitle className="flex items-center mb-2 sm:mb-0">
              <AlertCircle className="h-5 w-5 mr-2 text-red-500" />
              Мониторинг движения доступных велосипедов
            </CardTitle>
            <div className="flex gap-2">
              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Выберите период" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">За последний час</SelectItem>
                  <SelectItem value="4">За последние 4 часа</SelectItem>
                  <SelectItem value="12">За последние 12 часов</SelectItem>
                  <SelectItem value="24">За последние 24 часа</SelectItem>
                  <SelectItem value="48">За последние 48 часов</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={handleRefresh}>
                Обновить
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : error ? (
            <div className="text-center p-4 text-red-500">
              <AlertCircle className="h-10 w-10 mx-auto mb-2" />
              <p>{error}</p>
            </div>
          ) : bikes.length === 0 ? (
            <div className="text-center p-4 text-muted-foreground">
              Нет доступных велосипедов для мониторинга
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Модель</TableHead>
                  <TableHead>Серийный номер</TableHead>
                  <TableHead className="text-center">Перемещения</TableHead>
                  <TableHead>Последнее обновление</TableHead>
                  <TableHead className="text-center">Уровень тревоги</TableHead>
                  <TableHead className="text-right">Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bikes.filter(bike => bike.movement_count > 0).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                      Нет зафиксированных перемещений доступных велосипедов за выбранный период
                    </TableCell>
                  </TableRow>
                ) : (
                  bikes.map((bike) => (
                    <TableRow key={bike.id} className={
                      bike.alert_level === 'high' ? 'bg-red-50 dark:bg-red-950/20' :
                      bike.alert_level === 'medium' ? 'bg-amber-50 dark:bg-amber-950/20' :
                      bike.alert_level === 'low' && bike.movement_count > 0 ? 'bg-yellow-50 dark:bg-yellow-950/20' : 
                      ''
                    }>
                      <TableCell className="font-medium">{bike.model}</TableCell>
                      <TableCell>{bike.serial_number}</TableCell>
                      <TableCell className="text-center">{bike.movement_count}</TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {formatLastUpdate(bike.last_update)}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {getAlertBadge(bike.alert_level)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          size="sm" 
                          variant={bike.movement_count > 0 ? "destructive" : "outline"}
                          onClick={() => viewDetails(bike.id)}
                          disabled={bike.movement_count === 0}
                        >
                          {bike.movement_count > 0 ? 'Подробно' : 'Нет данных'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}

          <div className="mt-4 text-sm text-muted-foreground">
            <p>Этот отчет показывает только велосипеды со статусом <strong>"доступен"</strong>, у которых зафиксированы перемещения.</p>
            <p>Уровень тревоги: <span className="text-green-500 font-medium">Нет</span> - нет движений, 
              <span className="text-yellow-500 font-medium ml-1">Низкий</span> - 1-5 перемещений, 
              <span className="text-amber-500 font-medium ml-1">Средний</span> - 6-10 перемещений, 
              <span className="text-red-500 font-medium ml-1">Высокий</span> - более 10 перемещений
            </p>
          </div>
        </CardContent>
      </Card>

      <BikeMovementDetails 
        bikeId={selectedBikeId}
        open={showDetails}
        onClose={() => setShowDetails(false)}
        timeframe={timeframe}
      />
    </>
  );
};
